import * as React from 'react';
import {Container, Segment, Header, Dimmer, Loader, Label, GridColumn, Grid} from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { ComboBox } from '@progress/kendo-react-dropdowns';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import ReportsApi from '../../services/reports-service';
import { createBreadCrumbBasedOnPage } from '../../Utils/HelperFunctions';
import * as actionTypes from '../../store/action';
import { connect } from 'react-redux';
import '../Charts/DynamicGrid.css';
import {render} from "@testing-library/react"; // for consistent dark style
import GridContainer from "../GenericComponents/Grids/GridContainer";
import { Tab } from 'semantic-ui-react';
import DynamicReportGrid from './DynamicReportGrid';
import { ComputedDatum } from '@nivo/bar';


interface ChartLink {
    chartId: string;
    displayName: string;
    chartType: string;
    group?: string;
    supportsQuarter?: boolean;
    availableQuarters?: string[];
}

interface MyPieDatum {
    id: string;
    label: string;
    value: number;
    rawTotal?: number;
}
interface PieRawData {
    type: string;
    total: number;
}

interface ChartsLandingPageProps {
    loggedIn: boolean;
    setBreadCrumbPath: (breadCrumbPath: BreadCrumb[]) => void;
    setLoggedIn: (loggedIn: boolean) => void;
}
interface ChartConfig {
    keys?: string[];
    colors?: string | { scheme: string } | ((props: any) => string);
    indexBy?: string;
    legends: any[];
    axisLeftLegend: string;
}


interface ChartsLandingPageState {
    reports: ChartLink[];
    userAuthorized: boolean;
    pageLoading: boolean;
    selectedChartId?: string | null;
    isQuarterMode?: boolean;
    showCharts: boolean;
    group?: string;
    quarters?: string[];
    selectedQuarter?: string;
    chartData?: any | null;
    chartType?: string;
    lastSnapshot: string | null;
}



let chartConfigById: Record<string, ChartConfig> = {
    responsivenessIsdChart: {
        keys: ['In-Service Date Changes', 'No In-Service Date Changes'],
        indexBy: 'Owner',
        axisLeftLegend: 'Upgrades',
        colors: undefined, // colors function will be defined in the chart render
        legends: [
            {
                dataFrom: 'keys',
                anchor: 'bottom',
                direction: 'row',
                translateY: 120,
                itemWidth: 200,
                itemHeight: 20,
                itemsSpacing: 10,
                symbolSize: 20,
            },
        ],
    },
    responsivenessCostChangeChart: {
        keys: ['Cost Changes', 'No Cost Changes'],
        indexBy: 'Owner',
        axisLeftLegend: 'Upgrades',
        colors: undefined, // colors function will be defined in the chart render
        legends: [
            {
                dataFrom: 'keys',
                anchor: 'bottom',
                direction: 'row',
                translateY: 120,
                itemWidth: 150,
                itemHeight: 20,
                itemsSpacing: 10,
                symbolSize: 20,
            },
        ],
    },
    upgradeDelayedByTOChart: {
        keys: ['Projects'],
        indexBy: 'Owner',
        axisLeftLegend: 'Projects',
        colors: undefined, // colors function will be defined in the chart render
        legends: [
            {
                dataFrom: 'keys',
                anchor: 'bottom',
                direction: 'row',
                translateY: 120,
                itemWidth: 150,
                itemHeight: 20,
                itemsSpacing: 10,
                symbolSize: 20,
            },
        ],
    },
    portfolioSummaryFigure5Status: {
        axisLeftLegend: '',
        legends: [
            {
                anchor: 'bottom',
                direction: 'row',
                translateY: 70,
                itemWidth: 150,
                itemHeight: 20,
                itemsSpacing: 10,
                symbolSize: 18,
                symbolShape: 'circle',
            },
        ],
    },
    projectSummaryStatusFigure10: {
        axisLeftLegend: '',
        legends: [
            {
                anchor: 'left',
                direction: 'column',
                translateX: -120,
                itemWidth: 120,
                itemHeight: 18,
                itemsSpacing: 4,
                symbolSize: 14,
                symbolShape: 'circle',
            },
        ],
    },
    estimatedCostforUpgradesByISD: {
        keys: [], // set dynamically
        indexBy: "year",
        axisLeftLegend: "Estimated Cost (Millions)",

        // Nivo uses this for each stack segment
        colors: (datum) => {
            const key = datum.id as string;
            return (
                (datum.data as any)?.colors?.[key] ||  // preferred nested color map
                '#888'
            );
        },

        legends: [
            {
                dataFrom: 'keys',
                anchor: 'bottom',
                direction: 'row',
                translateY: 100,   // small push; large gap comes from margin
                itemWidth: 120,
                itemHeight: 20,
                itemsSpacing: 10,
                symbolSize: 20,
            },
        ],
    },
    estimatedCostforUpgradesByISDLast4Q: {
        keys: [], // set dynamically
        indexBy: "quarter",
        axisLeftLegend: "Estimated Cost (Millions)",

        // Nivo uses this for each stack segment
        colors: (datum) => {
            const key = datum.id as string;
            return (
                (datum.data as any)?.colors?.[key] ||  // preferred nested color map
                '#888'
            );
        },

        legends: [
            {
                dataFrom: 'keys',
                anchor: 'bottom',
                direction: 'row',
                translateY: 100,   // small push; large gap comes from margin
                itemWidth: 120,
                itemHeight: 20,
                itemsSpacing: 10,
                symbolSize: 20,
            },
        ],
    },
    countsForCompletedUpgradesByISDLast4Q: {
        keys: [], // set dynamically
        indexBy: "quarter",
        axisLeftLegend: "Count",

        // Nivo uses this for each stack segment
        colors: (datum) => {
            const key = datum.id as string;
            return (
                (datum.data as any)?.colors?.[key] ||  // preferred nested color map
                '#888'
            );
        },

        legends: [
            {
                dataFrom: 'keys',
                anchor: 'bottom',
                direction: 'row',
                translateY: 100,   // small push; large gap comes from margin
                itemWidth: 120,
                itemHeight: 20,
                itemsSpacing: 10,
                symbolSize: 20,
            },
        ],    },
    default: {
        keys: ['Value'],
        axisLeftLegend: 'Estimated Cost (Millions)',
        indexBy: 'Category',
        colors: ({ id }) => '#2F69BF',
        legends: [],
    },
};
const fmtMiles = (n: number) =>
    new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(n);

const pieChartConfigById: Record<
    string,
    { labelField: string; valueField: string }
> = {
    portfolioSummaryFigure5Status: { labelField: "Study Type", valueField: "Total" },
    projectSummaryStatusFigure10: { labelField: "Upgrade Status", valueField: "Total" },
    upgradeByTypeCostSummaryDonuts: { labelField: "type", valueField: "percentage" },
    default: { labelField: "Type", valueField: "Total" },
};

const resolveColors = (colors: any) => {
    if (!colors) return { scheme: "nivo" };

    // If it's already a color function
    if (typeof colors === "function") return colors;

    // If it's a string like "nivo" or "set2"
    if (typeof colors === "string") return { scheme: colors };

    // If it's { scheme: "nivo" }
    if (typeof colors === "object" && colors.scheme) return colors;

    // Default fallback
    return { scheme: "nivo" };
};

const exportToExcel = (data: any[], fileName: string = "report") => {
    if (!data || data.length === 0) {
        alert("No data available for export.");
        return;
    }

    // Extract headers
    const headers = Object.keys(data[0]);

    // Escape CSV values safely (handle commas, quotes, newlines)
    const escapeCsvValue = (value: any) => {
        if (value == null) return "";
        const str = value.toString().replace(/"/g, '""');
        return /[",\n]/.test(str) ? `"${str}"` : str;
    };

    // Build CSV rows
    const csvRows = [
        headers.join(","), // header row
        ...data.map(row => headers.map(h => escapeCsvValue(row[h])).join(",")),
    ];

    const csvContent = csvRows.join("\n");

    // Create a downloadable Blob
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    // Create a temporary download link
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};


class ChartsLandingPage extends React.Component<ChartsLandingPageProps, ChartsLandingPageState> {
    private reportsClient: ReportsApi;

    constructor(props: ChartsLandingPageProps) {
        super(props);
        this.state = {
            reports: [],
            userAuthorized: false,
            showCharts: true,
            pageLoading: true,
            lastSnapshot: null
        };
        this.reportsClient = new ReportsApi();
    }

    resetCharts = () => {
        this.setState({
            selectedChartId: null,
            chartData: null,
            pageLoading: false,
        });
    };

    resetReports = () => {
        this.setState({
            pageLoading: false,
        });
    };
    componentDidMount(): void {
        this.props.setBreadCrumbPath(
            createBreadCrumbBasedOnPage(window.location.pathname.split('/').filter(x => x))
        );
        this.getChartLinks();
        this.loadLastSnapshot();
    }


    handleSnapshotBaseData = async () => {
        this.setState({ pageLoading: true });

        try {
            const message = await this.reportsClient.snapshotBaseData();
            toast.success(message || "Snapshot created successfully!");

            // Load the latest snapshot timestamp after successful snapshot
            await this.loadLastSnapshot(); // <-- update state with new timestamp
        } catch (err: any) {
            const errorMessage = err?.toString() || "Error creating snapshot";
            toast.error(errorMessage);
            console.error(err);
        } finally {
            this.setState({ pageLoading: false });
        }
    };



    loadLastSnapshot = async () => {
        try {
            const timestamp = await this.reportsClient.getLastSnapshot();
            this.setState({ lastSnapshot: timestamp });
        } catch (err) {
            console.error("Failed to load last snapshot:", err);
            this.setState({ lastSnapshot: "Unavailable" });
        }
    };
    getChartLinks = () => {
        this.reportsClient
            .getChartDefinitions()
            .then((response: ChartLink[]) => {
                this.setState({ reports: response, userAuthorized: true, pageLoading: false });
            })
            .catch((error: any) => {
                if (error.response?.status === 403) {
                    this.setState({ userAuthorized: false, pageLoading: false });
                }
                if (!error.response?.data?.badToken) {
                    toast.error(`Error retrieving data from the server - ${error.response?.data?.message}`);
                } else {
                    this.props.setLoggedIn(false);
                }
            });
    };

    handleViewToggle = () => {
        this.setState(prev => ({ showCharts: !prev.showCharts, selectedChartId: undefined, chartData: undefined }));
    };

    handleChartChange = (chartId: string) => {
        const { reports, selectedQuarter } = this.state;
        if (!reports?.length) return;

        const chartDef = reports.find(c => c.chartId === chartId);
        if (!chartDef) return;

        this.setState({
            selectedChartId: chartId,
            chartType: chartDef.chartType || 'bar',
            isQuarterMode: chartDef.supportsQuarter,
            quarters: chartDef.supportsQuarter ? chartDef.availableQuarters || [] : [],
            selectedQuarter: chartDef.supportsQuarter ? selectedQuarter : undefined,
            chartData: undefined,
        });
    };

    handleQuarterChange = (e: any) => {
        this.setState({ selectedQuarter: e.value as string });
    };

    handleGoClick = async () => {
        const { selectedChartId, isQuarterMode, selectedQuarter, reports } = this.state;
        if (!selectedChartId) return toast.error('Please select a chart');

        const chartDef = reports.find(c => c.chartId === selectedChartId);
        if (!chartDef) return toast.error('Invalid chart selected');

        if (isQuarterMode && !selectedQuarter) return toast.error('Please select a quarter');

        this.setState({ pageLoading: true });

        try {
            const response = await this.reportsClient.getChartData(selectedChartId, selectedQuarter);
            const rawData = response?.data || response;

            let chartData = rawData;

            // 🥧 PIE CHART HANDLER
            if (chartDef.chartType === 'pie') {
                const config =
                    pieChartConfigById[chartDef.chartId] || pieChartConfigById["default"];

                const labelField = config.labelField;
                const valueField = config.valueField;

                // 🔸 Handle the "upgradeByTypeCostSummaryDonuts" custom donut layout
                if (chartDef.chartId === "upgradeByTypeCostSummaryDonuts") {
                    // Assume API returns array of objects like:
                    // [
                    //   { id: 'ITP', percentage: 74.9, color: '#00A38C', upgrades: 427, newLine: 1832.02, rebuild: 240.5, conversion: 111.4 },
                    //   { id: 'GI', percentage: 11.6, ... }
                    // ]
                    chartData = rawData.map((d: any) => ({
                        id: d.id ?? d[labelField],
                        label: d.id ?? d[labelField],
                        value: d[valueField],
                        remainder: 100 - d.percentage,
                        color: d.color,
                        upgrades: d.upgrades,
                        newLine: d.newLine,
                        rebuild: d.rebuild,
                        conversion: d.conversion
                    }));
                } else {
                    // 🔹 Standard pie charts
                    const totalSum = rawData.reduce(
                        (sum: number, d: any) => sum + (d[valueField] ?? 0),
                        0
                    );

                    chartData = rawData.map((d: any) => {
                        const label = d[labelField] ?? 'Unknown';
                        const value = d[valueField] ?? 0;

                        return {
                            id: label,
                            label,
                            value,
                            percentage: totalSum ? ((value / totalSum) * 100).toFixed(2) : '0',
                        };
                    });
                }
            }
            else if (chartDef.chartType === 'line') {
                chartData = [
                    {
                        id: chartDef.displayName || 'Series 1',
                        data: rawData.map((d: any) => ({
                            x: d["Study Type"] ?? d.type ?? 'Unknown',
                            y: d.TypeTotal ?? d.total ?? 0,
                        })),
                    },
                ];
            }

            else if (chartDef.chartId === 'estimatedCostforUpgradesByISD') {

                const allStudyTypes = new Set<string>();

                // discover dynamic numeric study-type keys
                rawData.forEach((row: Record<string, any>) => {
                    Object.keys(row).forEach(key => {
                        if (
                            key !== "IN_SERVICE_YEAR" &&
                            key !== "year" &&
                            key !== "STUDY_TYPE" &&
                            key !== "colors" &&
                            typeof row[key] === "number"
                        ) {
                            allStudyTypes.add(key);
                        }
                    });
                });

                rawData.forEach((row: Record<string, any>) => {
                    if (!row.colors) row.colors = {};   // Still needed to ensure the object exists

                    allStudyTypes.forEach(type => {
                        if (row[type] === undefined || row[type] === null) {
                            row[type] = 0;   // missing values should be 0
                        }
                    });
                });

                chartData = rawData;

                chartConfigById[chartDef.chartId].keys = Array.from(allStudyTypes);


                // chartData = fully normalized rows
                chartData = rawData;

                // Update chartConfig keys
                chartConfigById[chartDef.chartId].keys = Array.from(allStudyTypes);

            }
            else if (chartDef.chartId === 'estimatedCostforUpgradesByISDLast4Q' || chartDef.chartId === 'countsForCompletedUpgradesByISDLast4Q') {
                const allStudyTypes = new Set<string>();

                // discover dynamic numeric study-type keys
                rawData.forEach((row: Record<string, any>) => {
                    Object.keys(row).forEach(key => {
                        if (
                            key !== "IN_SERVICE_QUARTER" &&
                            key !== "quarter" &&
                            key !== "STUDY_TYPE" &&
                            key !== "colors" &&
                            typeof row[key] === "number"
                        ) {
                            allStudyTypes.add(key);
                        }
                    });
                });

                rawData.forEach((row: Record<string, any>) => {
                    if (!row.colors) row.colors = {};   // Still needed to ensure the object exists

                    allStudyTypes.forEach(type => {
                        if (row[type] === undefined || row[type] === null) {
                            row[type] = 0;   // missing values should be 0
                        }
                    });
                });

                chartData = rawData;

                chartConfigById[chartDef.chartId].keys = Array.from(allStudyTypes);


                // chartData = fully normalized rows
                chartData = rawData;

                // Update chartConfig keys
                chartConfigById[chartDef.chartId].keys = Array.from(allStudyTypes);

            }

            this.setState({
                chartData,
                chartType: chartDef.chartType || 'bar',
                pageLoading: false,
            });
        } catch (err) {
            console.error(err);
            toast.error('Error fetching chart data');
            this.setState({ pageLoading: false });
        }
    };

    renderChart = () => {
        const { chartType, chartData, selectedChartId } = this.state;
        if (!chartData || !chartType) return null;

        // Define config once, for all chart types
        const config = chartConfigById[selectedChartId || 'default'] || chartConfigById['default'];

        if (!chartData || chartData.length === 0) return null;
        if (selectedChartId === "upgradeByTypeCostSummaryDonuts") {
            return (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: "2rem",
                        marginTop: "2rem",
                    }}
                >
                    {chartData.map((item: any, i: number) => (
                        <div
                            key={i}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                textAlign: "center",
                            }}
                        >
                            <div style={{ width: 180, height: 180, position: "relative" }}>
                                <ResponsivePie
                                    data={[
                                        { id: item.label, value: item.value, color: item.color },
                                        { id: "remainder", value: item.remainder, color: "#e0e0e0" } // light gray background
                                    ]}
                                    colors={(datum) => datum.data.color}
                                    innerRadius={0.75}
                                    padAngle={1}
                                    cornerRadius={3}
                                    enableArcLabels={false}
                                    enableArcLinkLabels={false}
                                    isInteractive={false}
                                />
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: item.color || "#3B83C0",
                                    }}
                                >
                                    <div style={{ fontSize: "1.6rem", fontWeight: 700 }}>
                                        {item?.value != null ? item.value.toFixed(1) : "0.0"}%
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                        {item.id}
                                    </div>
                                </div>
                            </div>

                            <div
                                style={{
                                    marginTop: 10,
                                    fontSize: "0.85rem",
                                    lineHeight: 1.4,
                                }}
                            >
                                <div style={{ fontWeight: 600 }}>
                                    {item.upgrades} upgrades
                                </div>
                                <div>{fmtMiles(item.newLine)} miles of new line</div>
                                <div>{fmtMiles(item.rebuild)} miles of line rebuild</div>
                                <div>{fmtMiles(item.conversion)} miles of volt. conversion</div>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        switch (chartType) {
            case 'stackedbar': // ✅ Default stacked bar chart
                return (
                    <div style={{ height: 500, marginTop: 20 }}>
                        <div style={{ height: 500, marginTop: 20 }}>
                            <ResponsiveBar
                                data={chartData}
                                keys={config.keys || []}
                                indexBy={config.indexBy}
                                groupMode="stacked"
                                margin={{ top: 20, right: 20, bottom: 150, left: 60 }}
                                padding={0.3}
                                valueScale={{ type: 'linear' }}
                                indexScale={{ type: 'band', round: true }}

                                colors={(datum: ComputedDatum<any>) => {
                                    const key = datum.id as string;
                                    // support both flat columns (KEY_COLOR) and nested colors object
                                    return (
                                        // prefer nested colors map
                                        (datum.data as any)?.colors?.[key] ||
                                        (datum.data as any)?.[`${key}_COLOR`] ||
                                        '#888'
                                    );
                                }}

                                axisBottom={{
                                    tickRotation: -45,
                                    legend: config.indexBy,
                                    legendPosition: 'middle',
                                    legendOffset: 60,
                                }}
                                axisLeft={{
                                    legend: config.axisLeftLegend,
                                    legendPosition: 'middle',
                                    legendOffset: -50,
                                }}
                                labelSkipWidth={12}
                                labelSkipHeight={12}
                                labelTextColor={{ from: 'color' }}

                                legends={config.legends}
                            />

                        </div>
                    </div>
                );

            case 'line':
                return (
                    <div style={{ height: 500, marginTop: 20 }}>
                        <ResponsiveLine
                            data={chartData}
                            margin={{ top: 50, right: 50, bottom: 50, left: 60 }}
                            xScale={{ type: 'time', format: '%Y-%m-%d', precision: 'day' }}
                            xFormat="time:%Y-%m-%d"
                            yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                            axisBottom={{
                                format: '%b %d',
                                tickValues: 'every 1 month',
                            }}
                            axisLeft={{
                                legend: 'Value',
                                legendOffset: -40,
                                legendPosition: 'middle',
                            }}
                            colors={{ scheme: 'nivo' }}
                            pointSize={6}
                            pointBorderWidth={2}
                            pointBorderColor={{ from: 'serieColor' }}
                            useMesh={true}
                        />
                    </div>
                );
            case 'pie':
                const total = chartData.reduce((sum: number, d: any) => sum + d.value, 0);

                return (
                    <div style={{ height: 500, marginTop: 20 }}>
                        <ResponsivePie
                            data={chartData}
                            margin={{ top: 50, right: 160, bottom: 80, left: 160 }}
                            innerRadius={0.5}
                            padAngle={0.7}
                            cornerRadius={3}
                            colors={{ scheme: 'nivo' }}
                            borderWidth={1}
                            borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                            enableArcLabels={false} // keep inside slice labels hidden
                            enableArcLinkLabels={true} // show labels outside with $ amounts
                            arcLinkLabelsSkipAngle={4}
                            arcLinkLabelsTextColor="#333333"
                            arcLinkLabelsThickness={2}
                            arcLinkLabelsColor={{ from: 'color' }}
                            arcLinkLabel={d =>
                                `${d.label}: ${Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                    minimumFractionDigits: 0,
                                }).format(d.value as number)}`
                            }
                            legends={config.legends}
                            tooltip={({ datum }) => {
                                const percentage = ((datum.value / total) * 100).toFixed(2);
                                return (
                                    <div style={{ background: 'white', border: '1px solid #ccc', padding: '6px 10px', borderRadius: '4px' }}>
                                        <div><strong>{datum.label}</strong></div>
                                        <div>
                                            Value:{' '}
                                            {Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                                minimumFractionDigits: 0,
                                            }).format(datum.value as number)}
                                        </div>
                                        <div>Percentage: {percentage}%</div>
                                    </div>
                                );
                            }}

                            // ✅ Custom layer for centered total without removing existing labels
                            layers={['arcs', 'arcLinkLabels', 'legends', (props) => {
                                return (
                                    <text
                                        x={props.centerX}
                                        y={props.centerY}
                                        textAnchor="middle"
                                        dominantBaseline="central"
                                        style={{ fontSize: '24px', fontWeight: 700 }}
                                        fill="#333"
                                    >
                                        {Intl.NumberFormat('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                            minimumFractionDigits: 0,
                                        }).format(total)}
                                    </text>
                                );
                            }]}
                        />
                    </div>
                );
                default:
                return <div>No chart type defined for this chart ID.</div>;
        }
    };

    renderDynamicGrid = () => {
        const { chartData, selectedChartId } = this.state;
        if (!chartData || chartData.length === 0)
            return <p style={{ color: '#bbb' }}>No data available.</p>;

        const columns = Object.keys(chartData[0]);
        const formatCurrency = (val: any) =>
            val != null && !isNaN(val)
                ? `$${Number(val).toLocaleString()}`
                : '';

        // compute a sensible min width: 180px per column (adjust as needed)
        const perColumnWidth = 180;
        const minTableWidth = Math.max(columns.length * perColumnWidth, 1000);

        return (
            <DynamicReportGrid
                columns={columns}
                data={chartData}
                maxHeight="65vh" // optional — you can make it taller or shorter
            />
        );
    };
    render() {
        const {
            pageLoading,
            reports,
            selectedChartId,
            quarters,
            isQuarterMode,
            selectedQuarter,
            chartData
        } = this.state;

        // Separate chart-type vs grid-type reports
        const charts = reports.filter(r => r.chartType !== 'grid');
        const grids = reports.filter(r => r.chartType === 'grid');

        // Define Semantic UI Tabs
        const panes = [
            {
                menuItem: 'Charts',
                render: () => (
                    <Tab.Pane attached={false}>
                        <div style={{ marginBottom: 20 }}>
                            <label>Chart:</label>
                            <ComboBox
                                data={charts}
                                textField="displayName"
                                dataItemKey="chartId"
                                groupField="group"
                                value={charts.find(r => r.chartId === selectedChartId) || null}
                                onChange={e => this.handleChartChange(e.value.chartId)}
                                style={{ width: 500, marginLeft: 10 }}
                            />

                            <button onClick={this.handleGoClick} disabled={pageLoading} style={{ marginLeft: 10 }}>
                                {pageLoading ? 'Loading...' : 'Go'}
                            </button>
                        </div>

                        {isQuarterMode && (
                            <div style={{ marginBottom: 20 }}>
                                <label>Quarter:</label>
                                <ComboBox
                                    data={quarters || []}
                                    value={selectedQuarter}
                                    onChange={this.handleQuarterChange}
                                    style={{ width: 150, marginLeft: 10 }}
                                />
                            </div>
                        )}

                        {pageLoading ? (
                            <Dimmer active inverted>
                                <Loader>Loading...</Loader>
                            </Dimmer>
                        ) : (
                            <div style={{ marginTop: 20 }}>{this.renderChart()}</div>
                        )}
                    </Tab.Pane>
                ),
            },
            {
                menuItem: 'Reports',
                render: () => (
                    <Tab.Pane attached={false}>
                        <div style={{ marginBottom: 20 }}>
                            <label>Report:</label>
                            <ComboBox
                                data={grids}
                                textField="displayName"
                                dataItemKey="chartId"
                                groupField="group"
                                value={grids.find(r => r.chartId === selectedChartId) || null}
                                onChange={e => this.handleChartChange(e.value.chartId)}
                                style={{ width: 500, marginLeft: 10 }}
                            />
                            <button onClick={this.handleGoClick} disabled={pageLoading} style={{ marginLeft: 10 }}>
                                {pageLoading ? 'Loading...' : 'Go'}
                            </button>
                        </div>

                        {isQuarterMode && (
                            <div style={{ marginBottom: 20 }}>
                                <label>Quarter:</label>
                                <ComboBox
                                    data={quarters || []}
                                    value={selectedQuarter}
                                    onChange={this.handleQuarterChange}
                                    style={{ width: 150, marginLeft: 10 }}
                                />
                            </div>
                        )}
                        {pageLoading ? (
                            <Dimmer active inverted>
                                <Loader>Loading...</Loader>
                            </Dimmer>
                        ) : (
                            <div style={{ marginTop: 20 }}>{this.renderDynamicGrid()}</div>
                        )}
                    </Tab.Pane>
                ),
            },
        ];

        return (
            <div style={{ width: "100%", background: "#111", padding: "2rem" }}>
                <Segment basic style={{ background: "transparent" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Header as="h2" style={{ color: "#fff", margin: 0 }}>QPT Dashboard</Header>

                        {/* Vertical container for button + label */}
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                            <button
                                onClick={this.handleSnapshotBaseData}
                                disabled={this.state.pageLoading}
                                style={{
                                    background: "#2185d0",
                                    color: "white",
                                    border: "none",
                                    padding: "8px 14px",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                    fontSize: "0.9rem"
                                }}
                            >
                                {this.state.pageLoading ? "Working..." : "Snapshot Base Data"}
                            </button>

                            {/* Label directly beneath the button */}
                            <div style={{ marginTop: 8 }}>
                                <Label color="green" size="small">
                                    Last Snapshot:&nbsp;
                                    <strong>{this.state.lastSnapshot || "Loading..."}</strong>
                                </Label>
                            </div>
                        </div>
                    </div>
                    <Tab
                        menu={{ secondary: true, pointing: true, inverted: true }}
                        panes={panes}
                        onTabChange={(e, data) => {
                            if (data.activeIndex === 1) {
                                this.resetCharts();   // Charts → Reports
                            }
                            if (data.activeIndex === 0) {
                                this.resetReports();  // Reports → Charts
                            }
                        }}
                    />
                </Segment>
            </div>
        );
    }

}

    const mapStateToProps = (state: any) => {
    return {
        loggedIn: state.defaultReducer.loggedIn
    }};

    const mapDispatchToProps = (dispatch: any) => {
    return {
        setLoggedIn: (loggedIn: boolean) => dispatch(
            {type: actionTypes.LOGGED_IN, payload: loggedIn}),
        setBreadCrumbPath: (breadCrumbPath:BreadCrumb[]) => dispatch(
            {type: actionTypes.SET_BREADCRUMB_PATH, payload: breadCrumbPath})
    }
};

    export default connect(mapStateToProps, mapDispatchToProps)(ChartsLandingPage);

